import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { CarePlanDemographicSection } from "~/components/carePlan/CarePlanDemographicSection";
import { Grid } from "@mui/material";
import { ProblemListBox } from "~/components/carePlan/ProblemList/ProblemListBox";
import { getCarePlanForPatient } from "~/api/carePlan/getCarePlanForPatient";
import { MedicationListBox } from "~/components/carePlan/MedicationList/MedicationListBox";
import { TransportationListBox } from "~/components/carePlan/TransportationList/TransportationListBox";
import { NutritionListBox } from "~/components/carePlan/NutritionList/NutritionListBox";
import { PsychosocialListBox } from "~/components/carePlan/PsychosocialList/PsychosocialListBox";
import { FunctionalListBox } from "~/components/carePlan/FunctionalList/FunctionalListBox";
import { PatientGoalsBox } from "~/components/carePlan/PatientGoals/PatientGoalsBox";
import { InterventionsBox } from "~/components/carePlan/Interventions/InterventionsBox";
import { CarePlanActivityLog } from "~/components/carePlan/ActivityLog/CarePlanActivityLog";
import { CarePlanDataLog } from "~/components/carePlan/DataLog/CarePlanDataLog";
import { CarePlanActivityLogRpm } from './ActivityLog/CarePlanActivityLogRpm';
import { useGetCarePlanPresets } from '~/api/carePlan/getCarePlanPresets';
export const CarePlan = (props) => {
    const carePlanRef = useRef(null);
    const [monthValue, setMonthValue] = useState(props.initialDate ? dayjs(props.initialDate) : dayjs());
    const [demographics, setDemographics] = useState();
    const [problemList, setProblemList] = useState([]);
    const [medicationList, setMedicationList] = useState([]);
    const [transportationList, setTransportationList] = useState([]);
    const [nutritionList, setNutritionList] = useState([]);
    const [psychosocialList, setPsychosocialList] = useState([]);
    const [functionalList, setFunctionalList] = useState([]);
    const [patientGoals, setPatientGoals] = useState([]);
    const [interventions, setInterventions] = useState([]);
    const [reviewLogs, setReviewLogs] = useState([]);
    const [reviewLogsRpm, setReviewLogsRpm] = useState([]);
    const [dataLogs, setDataLogs] = useState([]);
    const [practiceName, setPracticeName] = useState('');
    const [disableAddButton, setDisableAddButton] = useState(false);
    const [firstEdit, setFirstEdit] = useState('');
    const [presets, setPresets] = useState([]);
    const { data } = useGetCarePlanPresets();
    const downloadPDF = async () => {
        if (!carePlanRef.current)
            return;
        try {
            console.log('Starting PDF generation via print...');
            // Create a style element for print
            const style = document.createElement('style');
            style.id = 'pdf-print-styles';
            style.innerHTML = `
      @media print {
        body * {
          visibility: hidden;
        }
        #care-plan-print-content,
        #care-plan-print-content * {
          visibility: visible;
        }
        #care-plan-print-content {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
        }
        /* Expand all scrollable containers for print */
        * {
          overflow: visible !important;
          height: auto !important;
          max-height: none !important;
        }
      }
    `;
            document.head.appendChild(style);
            // Add ID to the ref for targeting
            const oldId = carePlanRef.current.id;
            carePlanRef.current.id = 'care-plan-print-content';
            // Trigger print dialog
            window.print();
            // Cleanup
            carePlanRef.current.id = oldId || '';
            document.head.removeChild(style);
            console.log('Print dialog opened');
        }
        catch (error) {
            console.error('Error opening print dialog:', error);
            alert('There was an error opening the print dialog. Please try again.');
        }
    };
    useEffect(() => {
        if (props.onDownloadPDFReady) {
            props.onDownloadPDFReady(downloadPDF);
        }
    }, []);
    useEffect(() => {
        getCarePlan();
    }, [monthValue]);
    useEffect(() => {
        getCarePlanPresetsFunc();
    }, [data]);
    useEffect(() => {
        // Check if selected month is not the current month
        const isNotCurrentMonth = !monthValue.isSame(dayjs(), 'month') || !monthValue.isSame(dayjs(), 'year');
        setDisableAddButton(isNotCurrentMonth);
    }, [monthValue]);
    const getCarePlan = async () => {
        const response = await getCarePlanForPatient(props.patient.id, monthValue);
        setDemographics(response.demographics);
        setProblemList(response.problemList);
        setMedicationList(response.medicationList);
        setTransportationList(response.transportationList);
        setNutritionList(response.nutritionList);
        setPsychosocialList(response.psychosocialList);
        setFunctionalList(response.functionalList);
        setPatientGoals(response.patientGoals);
        setInterventions(response.interventions);
        setPracticeName(response.practiceName);
        setReviewLogs(response.reviewLogs);
        setReviewLogsRpm(response.reviewLogsRpm);
        setDataLogs(response.dataLogs);
        const firstEditValue = response.carePlan?.firstEdit;
        if (firstEditValue) {
            setFirstEdit(dayjs(firstEditValue).format('MMMM DD, YYYY'));
        }
    };
    const getCarePlanPresetsFunc = () => {
        setPresets(data?.presets ?? []);
    };
    const presetsByCategory = useMemo(() => {
        return presets.reduce((acc, preset) => {
            (acc[preset.category] ||= []).push(preset);
            return acc;
        }, {});
    }, [presets]);
    return (React.createElement("div", { style: { overflowY: 'scroll' }, ref: carePlanRef },
        React.createElement("div", { style: { display: 'flex', width: '100%', padding: '16px' } },
            React.createElement("img", { src: "/images/sweetspot-logo.svg", alt: "SweetSpot", width: "100", height: "17" }),
            React.createElement("div", { style: { marginLeft: 'auto', color: '#6B7280', fontSize: 12, fontFamily: 'Barlow', fontWeight: '500', textTransform: 'uppercase' } }, practiceName)),
        React.createElement("br", null),
        React.createElement("div", { style: { display: 'flex', alignItems: 'center' } },
            React.createElement("div", { style: { padding: '15px', color: 'black', fontSize: 20, fontFamily: 'Barlow', fontWeight: '600' } }, "Care Plan"),
            React.createElement("div", { style: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', padding: '15px' } },
                React.createElement("div", { style: { color: '#6B7280', fontSize: 12, fontFamily: 'Barlow', fontWeight: '500', textTransform: 'uppercase' } }, "Reporting Date"),
                React.createElement("div", null,
                    React.createElement(LocalizationProvider, { dateAdapter: AdapterDayjs },
                        React.createElement(DatePicker, { sx: { height: '40px', width: '175px' }, slotProps: {
                                textField: {
                                    size: 'small',
                                },
                            }, minDate: dayjs(new Date('2026-01-01')), maxDate: dayjs(new Date()), views: ['year', 'month'], value: dayjs(monthValue), onChange: (val) => {
                                if (val) {
                                    setMonthValue(dayjs(val));
                                }
                            } }))))),
        React.createElement("div", { style: { padding: '15px' } },
            React.createElement(CarePlanDemographicSection, { patient: props.patient, demographics: demographics, carePlanFirstEdit: firstEdit })),
        React.createElement("div", { style: { padding: '15px', color: 'black', fontSize: 16, fontFamily: 'Barlow', fontWeight: '600' } }, "Clinical Needs"),
        React.createElement(Grid, { container: true, spacing: 2, className: 'mb-2 items-center', style: { padding: '15px' } },
            React.createElement(Grid, { item: true, xs: 6, style: { height: '300px' } },
                React.createElement(ProblemListBox, { problemList: problemList, patientId: props.patient.id, isAddDisabled: disableAddButton, presets: presetsByCategory['problem'] ?? [] })),
            React.createElement(Grid, { item: true, xs: 6, style: { height: '300px' } },
                React.createElement(MedicationListBox, { medicationList: medicationList, patientId: props.patient.id, isAddDisabled: disableAddButton, presets: presetsByCategory['medication'] }))),
        React.createElement("div", { style: { padding: '15px', color: 'black', fontSize: 16, fontFamily: 'Barlow', fontWeight: '600' } }, "Other Needs (Social & Access to Care)"),
        React.createElement(Grid, { container: true, spacing: 2, className: 'mb-2 items-center', style: { padding: '15px' } },
            React.createElement(Grid, { item: true, xs: 6, style: { height: '300px' } },
                React.createElement(TransportationListBox, { transportationList: transportationList, patientId: props.patient.id, isAddDisabled: disableAddButton, presets: presetsByCategory['transportation'] })),
            React.createElement(Grid, { item: true, xs: 6, style: { height: '300px' } },
                React.createElement(NutritionListBox, { nutritionList: nutritionList, patientId: props.patient.id, isAddDisabled: disableAddButton, presets: presetsByCategory['nutrition'] })),
            React.createElement(Grid, { item: true, xs: 6, style: { height: '300px' } },
                React.createElement(PsychosocialListBox, { psychosocialList: psychosocialList, patientId: props.patient.id, isAddDisabled: disableAddButton, presets: presetsByCategory['psychosocial'] })),
            React.createElement(Grid, { item: true, xs: 6, style: { height: '300px' } },
                React.createElement(FunctionalListBox, { functionalList: functionalList, patientId: props.patient.id, isAddDisabled: disableAddButton, presets: presetsByCategory['functional'] }))),
        React.createElement("div", { style: { padding: '15px', color: 'black', fontSize: 16, fontFamily: 'Barlow', fontWeight: '600' } }, "Care Plan Objectives"),
        React.createElement(Grid, { container: true, spacing: 2, className: 'mb-2 items-center', style: { padding: '15px' } },
            React.createElement(Grid, { item: true, xs: 12, style: { height: '300px' } },
                React.createElement(PatientGoalsBox, { patientGoals: patientGoals, patientId: props.patient.id, isAddDisabled: disableAddButton, presets: presetsByCategory['goal'] }))),
        React.createElement(Grid, { container: true, spacing: 2, className: 'mb-2 items-center', style: { padding: '15px' } },
            React.createElement(Grid, { item: true, xs: 12, style: { height: '300px' } },
                React.createElement(InterventionsBox, { interventions: interventions, patientId: props.patient.id, isAddDisabled: disableAddButton, presets: presetsByCategory['intervention'] }))),
        props.patient.ccmConsent &&
            React.createElement(Grid, { container: true, spacing: 2, className: 'mb-2 items-center', style: { padding: '15px' } },
                React.createElement(Grid, { item: true, xs: 12, style: { height: '600px' } },
                    React.createElement(CarePlanActivityLog, { reviewLogs: reviewLogs, patientId: props.patient.id, monthValue: monthValue }))),
        props.patient.rpmConsent &&
            React.createElement(Grid, { container: true, spacing: 2, className: 'mb-2 items-center', style: { padding: '15px' } },
                React.createElement(Grid, { item: true, xs: 12, style: { height: '600px' } },
                    React.createElement(CarePlanActivityLogRpm, { reviewLogs: reviewLogsRpm, patientId: props.patient.id, monthValue: monthValue }))),
        React.createElement(Grid, { container: true, spacing: 2, className: 'mb-2 items-center', style: { padding: '15px' } },
            React.createElement(Grid, { item: true, xs: 12, style: { height: '600px' } },
                React.createElement(CarePlanDataLog, { dataLog: dataLogs, patientId: props.patient.id, monthValue: monthValue })))));
};
